import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import ProtectionStatusBadge from '../../components/cultivar/ProtectionStatusBadge';
import AuditTimeline from '../../components/cultivar/AuditTimeline';
import LicensorsPanel from '../../components/cultivar/LicensorsPanel';
import PropagatorTermsPanel from '../../components/cultivar/PropagatorTermsPanel';
import CultivarInfoPanel from '../../components/cultivar/CultivarInfoPanel';
import PbrMark from '../../components/cultivar/PbrMark';
import PbrNotice from '../../components/cultivar/PbrNotice';
import { RightsBadge, RightsNotice } from '../../components/cultivar/RightsNotice';
import IpgodCandidatesPanel from '../../components/cultivar/IpgodCandidatesPanel';
import { isPbrProtected } from '../../constants/pbr';
import {
  Leaf, Edit, Shield, Building2, MapPin, Calendar, FileText, ShoppingCart,
  Scale, Trees, BookOpen, History, Loader2, Award,
  AlertCircle, ExternalLink, ChevronRight, Plus, FileCode, Search, Trash2, Database,
  ImageIcon, Eye, Lock, Users, ShieldAlert, Tag
} from 'lucide-react';
import { quarantineSummary } from '../../utils/cultivarQuarantine';
import { MediaTab, AttributeValuesTab, EvaluatorsTab, VisibilityTab } from '../../components/cultivar/CultivarOwnerTabs';
import MaturityByRegionPanel from '../../components/cultivar/MaturityByRegionPanel';
import type { Cultivar, RegionalLicense, NurseryCultivar, ManagementGuide } from '../../types/cultivar';
import { PROGRAM_TYPE_LABELS, licenseTierShort } from '../../types/cultivar';
import cultivarService from '../../services/cultivar';
import pbrService, { type IpgodCandidate } from '../../services/pbr';
import { useAuth } from '../../context/AuthContext';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, formatDateInTz, formatLocalDateInTz } from '@/utils/timezone';

type TabKey = 'info' | 'licenses' | 'nurseries' | 'guides' | 'media' | 'observations' | 'evaluators' | 'visibility' | 'audit';

function CultivarDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const orgTz = useOrgTimezone();

  const [cultivar, setCultivar] = useState<Cultivar | null>(null);
  const [licenses, setLicenses] = useState<RegionalLicense[]>([]);
  const [nurseries, setNurseries] = useState<NurseryCultivar[]>([]);
  const [guides, setGuides] = useState<ManagementGuide[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lookupRunning, setLookupRunning] = useState(false);
  const [ipgodRunning, setIpgodRunning] = useState(false);
  const [lookupResult, setLookupResult] = useState<string | null>(null);
  const [ipgodCandidates, setIpgodCandidates] = useState<IpgodCandidate[]>([]);

  // 'maturity' was its own tab until maturity was recognised as an evaluation
  // reading rather than a parallel record. Old links and bookmarks still carry
  // it, so they land on the section that absorbed it.
  const rawTab = searchParams.get('tab');
  const activeTab: TabKey = rawTab === 'maturity' ? 'observations' : ((rawTab as TabKey) || 'info');

  useEffect(() => {
    if (id) {
      loadCultivar(Number(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadCultivar = async (cultivarId: number) => {
    setLoading(true);
    setError(null);

    try {
      const response = await cultivarService.getCultivar(cultivarId);
      if (response.success && response.data) {
        setCultivar(response.data);
        // Load related data in parallel
        loadRelatedData(cultivarId);
      } else {
        setError(response.message || 'Cultivar not found');
      }
    } catch (err) {
      console.error('Error loading cultivar:', err);
      setError('Failed to load cultivar. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedData = async (cultivarId: number) => {
    try {
      const [licensesRes, nurseriesRes, guidesRes] = await Promise.all([
        cultivarService.getLicensesForCultivar(cultivarId),
        cultivarService.getNurseriesForCultivar(cultivarId),
        cultivarService.getGuidesForCultivar(cultivarId),
      ]);

      if (licensesRes.success && licensesRes.data) {
        setLicenses(licensesRes.data);
      }
      if (nurseriesRes.success && nurseriesRes.data) {
        setNurseries(nurseriesRes.data);
      }
      if (guidesRes.success && guidesRes.data) {
        setGuides(guidesRes.data);
      }
    } catch (err) {
      console.error('Error loading related data:', err);
    }
  };

  const setActiveTab = (tab: TabKey) => {
    setSearchParams({ tab }, { replace: true });
  };

  // Per-org editing: pbr or admin licence + creator-org.
  const { hasAccess: hasWriteLicence } = useModuleLicense('cultivar', ['pbr', 'admin']);
  // Admin licence unlocks permanent delete (still per-org).
  const { hasAccess: hasAdminLicence } = useModuleLicense('cultivar', 'admin');
  const isOwnOrg = cultivar?.created_by_org_id === user?.organisation_id;
  const canEdit = hasWriteLicence && isOwnOrg;
  const canHardDelete = hasAdminLicence && isOwnOrg;
  const [hardDeleting, setHardDeleting] = useState(false);

  const handleHardDelete = async () => {
    if (!id || !cultivar) return;
    const confirmed = window.confirm(
      `Permanently delete "${cultivar.name}"?\n\n` +
      `This cannot be undone. All licences, protections, declarations, programs, ` +
      `guides, and PBR application records for this cultivar will also be deleted.\n\n` +
      `If the cultivar is referenced by nursery stock items or planted cultivar ` +
      `areas, the deletion will be blocked.`
    );
    if (!confirmed) return;
    setHardDeleting(true);
    try {
      await cultivarService.permanentlyDeleteCultivar(Number(id));
      navigate('/cultivar');
    } catch (e: any) {
      const details = e?.response?.data?.error?.details as
        | { nursery_stock_items?: number; cultivar_areas?: number }
        | undefined;
      if (details && (details.nursery_stock_items || details.cultivar_areas)) {
        const parts: string[] = [];
        if (details.nursery_stock_items) parts.push(`${details.nursery_stock_items} nursery stock item(s)`);
        if (details.cultivar_areas) parts.push(`${details.cultivar_areas} planted area(s)`);
        window.alert(`Cannot delete: cultivar is referenced by ${parts.join(' and ')}. Remove them first.`);
      } else {
        window.alert(e?.response?.data?.error?.message || e?.message || 'Delete failed');
      }
    } finally {
      setHardDeleting(false);
    }
  };
  // Backend sets viewer_is_owner=true iff the viewer's org has ≥1 row in
  // cultivar_licensors for this cultivar (any region). Only then do we show
  // the licensor action buttons.
  const isLicensor = !!((cultivar as any)?.viewer_is_owner);
  const isCreatorNoLicensor =
    !isLicensor && cultivar?.created_by_org_id === user?.organisation_id;

  const formatDate = (date: string | null | undefined) => {
    if (!date) return 'N/A';
    return formatDateInTz(date, orgTz);
  };

  const quarantineText = cultivar ? quarantineSummary(cultivar, (d) => formatLocalDateInTz(d, orgTz)) : null;

  const ownerTabs: { key: TabKey; label: string; icon: typeof Leaf; count?: number }[] = canEdit ? [
    { key: 'media', label: 'Media', icon: ImageIcon, count: cultivar?.media?.length },
    { key: 'observations', label: 'Observations', icon: Eye, count: cultivar?.attribute_values?.length },
    { key: 'evaluators', label: 'Evaluators', icon: Users },
    { key: 'visibility', label: 'Visibility', icon: Lock },
  ] : [
    { key: 'media', label: 'Media', icon: ImageIcon, count: cultivar?.media?.length },
    { key: 'observations', label: 'Observations', icon: Eye, count: cultivar?.attribute_values?.length },
  ];

  const tabs: { key: TabKey; label: string; icon: typeof Leaf; count?: number }[] = [
    { key: 'info', label: 'Overview', icon: FileText },
    { key: 'licenses', label: 'Licenses', icon: Scale, count: licenses.length },
    { key: 'nurseries', label: 'Nurseries', icon: Trees, count: nurseries.length },
    { key: 'guides', label: 'Guides', icon: BookOpen, count: guides.length },
    ...ownerTabs,
    { key: 'audit', label: 'History', icon: History },
  ];

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Cultivar Details"
          icon={<Leaf className="h-6 w-6 text-white" />}
        />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-10 w-10 text-primary-600 animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  // Error State
  if (error || !cultivar) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Cultivar Details"
          icon={<Leaf className="h-6 w-6 text-white" />}
        />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <CardContent className="p-12 text-center">
              <AlertCircle className="h-12 w-12 text-error-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-neutral-900 mb-2">
                Cultivar Not Found
              </h2>
              <p className="text-neutral-600 mb-6">{error || 'The requested cultivar could not be found.'}</p>
              <Button variant="primary" onClick={() => navigate('/cultivar')}>
                Back to Registry
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={cultivar.name}
        subtitle={
          cultivar.trade_name
            ? `Trade name: ${cultivar.trade_name}`
            : cultivar.botanical_name ?? undefined
        }
        icon={<Leaf className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex flex-wrap gap-2">
            {canEdit && (
              <Button variant="outline" onClick={() => navigate(`/cultivar/${id}/edit`)}>
                <Edit className="h-4 w-4" />
                Edit Cultivar
              </Button>
            )}
            {canEdit && id && (
              <Button
                variant="outline"
                disabled={lookupRunning}
                onClick={async () => {
                  if (!confirm(
                    'Run an automated PBR lookup? This searches public registries (IP Australia, UPOV, USPTO, CPVO, NZ PVR) via the Anthropic API and updates protection records in place. Cultivar fields are only written where currently blank.'
                  )) return;
                  setLookupRunning(true);
                  setLookupResult(null);
                  try {
                    const r = await pbrService.runCultivarLookup(Number(id));
                    if (r.success && r.data) {
                      const np = r.data.applied.protections.length;
                      const nc = Object.keys(r.data.applied.cultivar_updates).length;
                      const searches = r.data.web_searches_used;
                      const contextBits = [
                        typeof searches === 'number'
                          ? `${searches} web search${searches === 1 ? '' : 'es'}`
                          : null,
                        r.data.model || null,
                      ].filter(Boolean);
                      const contextSuffix = contextBits.length > 0
                        ? ` (${contextBits.join(' | ')})`
                        : '';

                      let message = np === 0 && nc === 0
                        ? `No matching protections or cultivar updates found${contextSuffix}.`
                        : `Applied ${np} protection${np === 1 ? '' : 's'}${nc > 0 ? ` + ${nc} cultivar field${nc === 1 ? '' : 's'}` : ''}${contextSuffix}.`;

                      if (r.data.warning) {
                        message += ` ${r.data.warning}`;
                      }
                      // Best-effort refresh only; don't mask a successful lookup if refresh fails.
                      try {
                        const refreshed = await cultivarService.getCultivar(Number(id));
                        if (refreshed.success && refreshed.data) setCultivar(refreshed.data);
                      } catch (refreshErr) {
                        console.warn('PBR lookup applied but cultivar refresh failed:', refreshErr);
                        message += ' Lookup succeeded, but refresh failed. Reload this page to view the latest data.';
                      }
                      setLookupResult(`PBR lookup: ${message}`);
                    } else {
                      setLookupResult(`Error: ${r.message || 'Lookup failed'}`);
                    }
                  } catch (e) {
                    setLookupResult(`Error: ${e instanceof Error ? e.message : 'Lookup failed'}`);
                  } finally {
                    setLookupRunning(false);
                  }
                }}
              >
                {lookupRunning
                  ? <><Loader2 className="h-4 w-4 animate-spin" /> Searching…</>
                  : <><Search className="h-4 w-4" /> PBR lookup</>}
              </Button>
            )}
            {canEdit && id && (
              <Button
                variant="outline"
                disabled={ipgodRunning}
                onClick={async () => {
                  if (!confirm(
                    'Sync against the IP Australia PBR registry (data.gov.au IPGOD)? This is a free, exact-match lookup of Australian PBR records only — no international jurisdictions, and the dataset trails the live registry by ~2 years. Exact denomination matches are written in place; weaker matches are listed for review only.'
                  )) return;
                  setIpgodRunning(true);
                  setLookupResult(null);
                  setIpgodCandidates([]);
                  try {
                    const r = await pbrService.runIpgodSync(Number(id));
                    if (r.success && r.data) {
                      const np = r.data.applied.protections.length;
                      const nc = Object.keys(r.data.applied.cultivar_updates).length;
                      const ncand = r.data.candidates.length;
                      let message = np === 0 && nc === 0
                        ? `No exact AU PBR match found in ${r.data.dataset}.`
                        : `Applied ${np} AU PBR protection${np === 1 ? '' : 's'}${nc > 0 ? ` + ${nc} cultivar field${nc === 1 ? '' : 's'}` : ''} from ${r.data.dataset}.`;
                      if (ncand > 0) {
                        message += ` ${ncand} close match${ncand === 1 ? '' : 'es'} to review below.`;
                        setIpgodCandidates(r.data.candidates);
                      }
                      try {
                        const refreshed = await cultivarService.getCultivar(Number(id));
                        if (refreshed.success && refreshed.data) setCultivar(refreshed.data);
                      } catch (refreshErr) {
                        console.warn('IPGOD sync applied but cultivar refresh failed:', refreshErr);
                        message += ' Sync succeeded, but refresh failed. Reload this page to view the latest data.';
                      }
                      setLookupResult(`AU PBR registry: ${message}`);
                    } else {
                      setLookupResult(`AU PBR registry — Error: ${r.message || 'Sync failed'}`);
                    }
                  } catch (e) {
                    setLookupResult(`AU PBR registry — Error: ${e instanceof Error ? e.message : 'Sync failed'}`);
                  } finally {
                    setIpgodRunning(false);
                  }
                }}
              >
                {ipgodRunning
                  ? <><Loader2 className="h-4 w-4 animate-spin" /> Syncing…</>
                  : <><Database className="h-4 w-4" /> AU PBR registry</>}
              </Button>
            )}
            {isLicensor && (
              <>
                <Button variant="outline" onClick={() => navigate('/cultivar/contracts')}>
                  <FileCode className="h-4 w-4" />
                  Contracts
                </Button>
                <Button variant="outline" onClick={() => setActiveTab('licenses')}>
                  <Scale className="h-4 w-4" />
                  Show Licences
                </Button>
                <Button variant="primary" onClick={() => navigate(`/cultivar/${id}/licences/new`)}>
                  <Plus className="h-4 w-4" />
                  Issue Licence
                </Button>
              </>
            )}
            {isCreatorNoLicensor && (
              <Button variant="primary" onClick={() => setActiveTab('licenses')}>
                <Plus className="h-4 w-4" />
                Claim licensor rights
              </Button>
            )}
            {cultivar.viewer_tier === 'nursery_licensee' && hasWriteLicence && id && (
              <Button
                onClick={() => navigate(`/cultivar/orders/new?cultivar_id=${id}`)}
              >
                <ShoppingCart className="h-4 w-4" />
                Place order
              </Button>
            )}
            {canHardDelete && (
              <Button
                variant="outline"
                disabled={hardDeleting}
                onClick={handleHardDelete}
                className="!text-error-600 hover:!bg-error-50 !border-error-300"
              >
                {hardDeleting
                  ? <><Loader2 className="h-4 w-4 animate-spin" /> Deleting…</>
                  : <><Trash2 className="h-4 w-4" /> Delete permanently</>}
              </Button>
            )}
          </div>
        }
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/*
          PBR labelling block — IP Australia "Industry guidelines for labelling"
          require the PBR symbol to appear immediately next to the variety name
          AND the verbatim warning text to be present on the same view. Both
          components auto-hide when the cultivar is not under PBR protection,
          so this entire block effectively renders only for protected cultivars.
        */}
        {isPbrProtected(cultivar.protection_status) && (
          <div className="mb-6 rounded-lg border border-neutral-200 bg-white p-4">
            <div className="text-lg font-semibold text-neutral-900">
              {cultivar.name}
              <PbrMark cultivar={cultivar} />
            </div>
            {cultivar.botanical_name && (
              <div className="mt-1 text-sm italic text-neutral-600">{cultivar.botanical_name}</div>
            )}
            <PbrNotice cultivar={cultivar} className="mt-3" />
          </div>
        )}
        <RightsNotice
          state={cultivar.rights_state}
          audience={cultivar.viewer_is_owner ? 'owner' : 'licensee'}
          licensor={cultivar.created_by_org_name}
          cultivar={cultivar.name}
          note={cultivar.rights_review_note}
          className="mb-6"
        />
        {lookupResult && (
          <div className={`mb-4 p-3 rounded border text-sm ${lookupResult.includes('Error:') ? 'bg-red-50 border-red-200 text-red-700' : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`}>
            {lookupResult}
          </div>
        )}
        {canEdit && id && ipgodCandidates.length > 0 && (
          <IpgodCandidatesPanel
            cultivarId={Number(id)}
            cultivarName={cultivar.name}
            candidates={ipgodCandidates}
            existingAuPbrNumber={
              cultivar.protections?.find(p => p.jurisdiction === 'AU' && p.type === 'pbr')?.registration_number ?? null
            }
            onApplied={async () => {
              try {
                const refreshed = await cultivarService.getCultivar(Number(id));
                if (refreshed.success && refreshed.data) setCultivar(refreshed.data);
              } catch {
                // The protection is saved; a reload shows it.
              }
            }}
            onClose={() => setIpgodCandidates([])}
          />
        )}
        {canEdit && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-neutral-200 bg-white p-3 text-sm text-neutral-700">
            {cultivar.is_public || cultivar.is_open_to_propagate ? (
              <Users className="h-4 w-4 mt-0.5 shrink-0 text-primary-600" />
            ) : (
              <Lock className="h-4 w-4 mt-0.5 shrink-0 text-neutral-500" />
            )}
            <p className="min-w-0">
              {cultivar.is_open_to_propagate
                ? 'Open: every organisation can see the full record and grow or propagate it without a licence.'
                : cultivar.is_public
                  ? 'Public: every organisation can find a listing — name, crop, breeder and protection — and needs a licence from you for more.'
                  : 'Private: only your organisation, and anyone you license it to, can see this cultivar.'}
              {' '}
              <button
                type="button"
                className="text-primary-700 underline"
                onClick={() => navigate(`/cultivar/${id}/edit`)}
              >
                Change
              </button>
            </p>
          </div>
        )}
        {/* Quick Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-neutral-200 p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-success-100 flex items-center justify-center">
                <Shield className="h-5 w-5 text-success-600" />
              </div>
              <div>
                <p className="text-xs text-neutral-500 uppercase tracking-wide">Protection</p>
                <ProtectionStatusBadge status={cultivar.protection_status} size="md" showIcon={false} />
                <RightsBadge state={cultivar.rights_state} className="mt-1" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-neutral-200 p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
                <Scale className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <p className="text-xs text-neutral-500 uppercase tracking-wide">Licenses</p>
                <p className="text-lg font-bold text-neutral-900">{licenses.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-neutral-200 p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Trees className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-neutral-500 uppercase tracking-wide">Nurseries</p>
                <p className="text-lg font-bold text-neutral-900">{nurseries.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-neutral-200 p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <BookOpen className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-neutral-500 uppercase tracking-wide">Guides</p>
                <p className="text-lg font-bold text-neutral-900">{guides.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-neutral-200 mb-6">
          <nav className="flex gap-1 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;

              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`
                    flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
                    ${isActive
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
                    }
                  `}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                  {tab.count !== undefined && tab.count > 0 && (
                    <span className={`
                      px-1.5 py-0.5 text-xs rounded-full
                      ${isActive ? 'bg-primary-100 text-primary-700' : 'bg-neutral-100 text-neutral-600'}
                    `}>
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div>
          {/* Overview Tab */}
          {activeTab === 'info' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Info */}
              <div className="lg:col-span-2 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Cultivar Information</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <p className="text-sm text-neutral-500 mb-1">Botanical Name</p>
                        <p className="font-medium text-neutral-900">
                          {cultivar.botanical_name || 'Not specified'}
                        </p>
                      </div>
                      {/* Legacy free-text species. The form no longer collects it —
                          Crop Type below says the same thing — so only show it when an
                          older record carries something Crop Type doesn't already say. */}
                      {cultivar.species &&
                        cultivar.species.trim().toLowerCase() !==
                          (cultivar.crop_type_name || '').trim().toLowerCase() && (
                        <div>
                          <p className="text-sm text-neutral-500 mb-1">Species</p>
                          <p className="font-medium text-neutral-900">{cultivar.species}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-sm text-neutral-500 mb-1">Crop Type</p>
                        <p className="font-medium text-neutral-900">
                          {cultivar.crop_type_name || 'Not specified'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-neutral-500 mb-1">Year Bred</p>
                        <p className="font-medium text-neutral-900">
                          {cultivar.year_bred || 'Not specified'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-neutral-500 mb-1">Chill requirement</p>
                        <p className="font-medium text-neutral-900">
                          {cultivar.chill_requirement_portions != null
                            ? `${cultivar.chill_requirement_portions} chill portions`
                            : 'Not specified'}
                        </p>
                      </div>
                    </div>

                    {cultivar.description && (
                      <div className="mt-6 pt-6 border-t border-neutral-100">
                        <p className="text-sm text-neutral-500 mb-2">Description</p>
                        <p className="text-neutral-700">{cultivar.description}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <CultivarInfoPanel
                  cultivar={cultivar}
                  canEdit={canEdit}
                  canResearch={hasAdminLicence && isOwnOrg}
                  onSaved={(info) => setCultivar((c) => (c ? { ...c, ...info } : c))}
                />

              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* IP Protection Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="h-5 w-5 text-primary-600" />
                      IP Protection
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {cultivar.protections && cultivar.protections.length > 0 ? (
                        cultivar.protections.map((p) => {
                          const colorMap: Record<string, string> = {
                            pbr: 'success', plant_patent: 'primary', cpvo: 'primary',
                            pvr: 'primary', upov: 'neutral', other: 'neutral'
                          };
                          const color = colorMap[p.type] || 'neutral';
                          const labelMap: Record<string, string> = {
                            pbr: "Plant Breeder's Rights", plant_patent: 'Plant Patent',
                            cpvo: 'EU Community Plant Variety', pvr: 'Plant Variety Rights',
                            upov: 'UPOV Registration', other: 'Other Protection'
                          };
                          const statusBadgeMap: Record<string, { label: string; classes: string }> = {
                            granted:          { label: 'Granted',          classes: 'bg-success-100 text-success-700' },
                            accepted:         { label: 'Accepted',         classes: 'bg-warning-100 text-warning-700' },
                            filed:            { label: 'Filed',            classes: 'bg-primary-100 text-primary-700' },
                            under_examination:{ label: 'Under Examination',classes: 'bg-primary-100 text-primary-700' },
                            ceased:           { label: 'Ceased',           classes: 'bg-neutral-100 text-neutral-500' },
                            rejected:         { label: 'Rejected',         classes: 'bg-error-100 text-error-700' },
                          };
                          const badge = statusBadgeMap[p.status] ?? { label: p.status, classes: 'bg-neutral-100 text-neutral-500' };
                          return (
                            <div key={p.id} className={`bg-${color}-50 rounded-lg p-4`}>
                              <div className="flex items-start justify-between mb-1">
                                <p className={`text-xs text-${color}-600 font-medium uppercase tracking-wide`}>
                                  {p.jurisdiction} — {labelMap[p.type] || p.type}
                                </p>
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${badge.classes}`}>
                                  {badge.label}
                                </span>
                              </div>
                              {p.registration_number && (
                                <p className={`font-bold text-${color}-900`}>{p.registration_number}</p>
                              )}
                              {p.authority && (
                                <p className={`text-sm text-${color}-700 mt-1`}>Authority: {p.authority}</p>
                              )}
                              {p.application_date && !p.grant_date && (
                                <p className={`text-sm text-${color}-700`}>Applied: {formatDate(p.application_date)}</p>
                              )}
                              {p.acceptance_date && !p.grant_date && (
                                <p className={`text-sm text-${color}-700`}>Accepted: {formatDate(p.acceptance_date)}</p>
                              )}
                              {p.grant_date && (
                                <p className={`text-sm text-${color}-700`}>Granted: {formatDate(p.grant_date)}</p>
                              )}
                              {p.expiry_date && (
                                <p className={`text-sm text-${color}-700`}>Expires: {formatDate(p.expiry_date)}</p>
                              )}
                              {p.source_url && (
                                <a href={p.source_url} target="_blank" rel="noopener noreferrer"
                                   className={`text-xs text-${color}-600 underline mt-1 inline-block`}>
                                  Source
                                </a>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-sm text-neutral-500">
                          {cultivar.protection_status === 'open'
                            ? 'This is an open cultivar with no IP protection.'
                            : 'No protection registrations recorded.'}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Origin Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-neutral-500" />
                      Origin & Ownership
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Building2 className="h-4 w-4 text-neutral-400" />
                        <div>
                          <p className="text-xs text-neutral-500">Breeder</p>
                          <p className="font-medium text-neutral-900">
                            {cultivar.breeder_org_name || cultivar.breeder_name || 'Not specified'}
                          </p>
                        </div>
                      </div>
                      {cultivar.origin_country && (
                        <div className="flex items-center gap-3">
                          <MapPin className="h-4 w-4 text-neutral-400" />
                          <div>
                            <p className="text-xs text-neutral-500">Origin</p>
                            <p className="font-medium text-neutral-900">{cultivar.origin_country}</p>
                          </div>
                        </div>
                      )}
                      {quarantineText && (
                        <div className="flex items-start gap-3">
                          <ShieldAlert className="h-4 w-4 text-neutral-400 mt-0.5" />
                          <div className="min-w-0">
                            <p className="text-xs text-neutral-500">Quarantine</p>
                            <p className="font-medium text-neutral-900">{quarantineText}</p>
                            {cultivar.quarantine_permit_number && (
                              <p className="text-xs text-neutral-500 break-words">
                                Import permit {cultivar.quarantine_permit_number}
                              </p>
                            )}
                            {cultivar.quarantine_notes && (
                              <p className="text-xs text-neutral-500 whitespace-pre-line break-words">{cultivar.quarantine_notes}</p>
                            )}
                          </div>
                        </div>
                      )}
                      {cultivar.legacy_code && (
                        <div className="flex items-center gap-3">
                          <Tag className="h-4 w-4 text-neutral-400" />
                          <div>
                            <p className="text-xs text-neutral-500">Legacy code</p>
                            <p className="font-medium text-neutral-900 break-all">{cultivar.legacy_code}</p>
                          </div>
                        </div>
                      )}
                      {cultivar.programs && cultivar.programs.length > 0 && (
                        <div className="flex items-start gap-3">
                          <Award className="h-4 w-4 text-primary-500 mt-0.5" />
                          <div>
                            <p className="text-xs text-neutral-500">
                              {cultivar.programs.length === 1 ? 'Program' : 'Programs'}
                            </p>
                            {cultivar.programs.map((prog) => (
                              <p key={prog.id} className="font-medium text-neutral-900">
                                {prog.name}
                                <span className="ml-1 text-xs font-normal text-neutral-500">
                                  {prog.program_type === 'other' && prog.custom_type_label
                                    ? prog.custom_type_label
                                    : PROGRAM_TYPE_LABELS[prog.program_type]}
                                  {prog.organisation_name ? ` · ${prog.organisation_name}` : ''}
                                </span>
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-3">
                        <Calendar className="h-4 w-4 text-neutral-400" />
                        <div>
                          <p className="text-xs text-neutral-500">Registered</p>
                          <p className="font-medium text-neutral-900">
                            {formatDate(cultivar.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Licenses Tab */}
          {activeTab === 'licenses' && (
            <div className="space-y-6">
            <LicensorsPanel
              cultivarId={Number(id)}
              canManage={isLicensor || isCreatorNoLicensor}
              onChange={() => loadCultivar(Number(id))}
            />
            <PropagatorTermsPanel
              cultivarId={Number(id)}
              canManage={isLicensor}
            />
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Regional Licenses</CardTitle>
                <Button variant="primary" size="sm" onClick={() => navigate(`/cultivar/licenses?cultivar_id=${id}`)}>
                  View All Licenses
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent>
                {licenses.length === 0 ? (
                  <div className="text-center py-12">
                    <Scale className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
                    <p className="text-neutral-500">No licenses registered for this cultivar</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {licenses.slice(0, 5).map((license) => (
                      <div
                        key={license.id}
                        className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg hover:bg-neutral-100 transition-colors cursor-pointer"
                        onClick={() => navigate(`/cultivar/licences/${license.id}/agreement`)}
                      >
                        <div>
                          <p className="font-medium text-neutral-900">
                            {license.licensee_org_name}
                          </p>
                          <p className="text-sm text-neutral-500">
                            {licenseTierShort(license.license_tier)} • {license.region || license.country || 'Global'}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`
                            px-2 py-1 text-xs font-medium rounded-full
                            ${license.status === 'active'
                              ? 'bg-success-100 text-success-700'
                              : license.status === 'pending_confirmation'
                              ? 'bg-warning-100 text-warning-700'
                              : 'bg-neutral-100 text-neutral-700'
                            }
                          `}>
                            {license.status.replace('_', ' ')}
                          </span>
                          <ChevronRight className="h-4 w-4 text-neutral-400" />
                        </div>
                      </div>
                    ))}
                    {licenses.length > 5 && (
                      <p className="text-sm text-neutral-500 text-center pt-2">
                        And {licenses.length - 5} more licenses...
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            </div>
          )}

          {/* Nurseries Tab */}
          {activeTab === 'nurseries' && (
            <Card>
              <CardHeader>
                <CardTitle>Nursery Suppliers</CardTitle>
              </CardHeader>
              <CardContent>
                {cultivar.is_open_to_propagate && (
                  <div className="mb-4 rounded-lg p-4 bg-success-50 border border-success-200 flex items-start gap-3">
                    <Leaf className="h-5 w-5 mt-0.5 text-success-600 shrink-0" />
                    <div>
                      <p className="font-medium text-success-800">Open cultivar — no licence required</p>
                      <p className="text-sm mt-1 text-success-700">
                        This cultivar has no IP protection and is not part of a program, so any nursery may
                        propagate and supply it freely. Nurseries below are self-listed suppliers.
                      </p>
                    </div>
                  </div>
                )}
                {nurseries.length === 0 ? (
                  <div className="text-center py-12">
                    <Trees className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
                    <p className="text-neutral-500">
                      {cultivar.is_open_to_propagate
                        ? 'No nurseries have listed themselves as suppliers yet'
                        : 'No nurseries supply this cultivar yet'}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {nurseries.map((nursery) => (
                      <div
                        key={nursery.id}
                        className="p-4 bg-neutral-50 rounded-lg"
                      >
                        <p className="font-medium text-neutral-900 mb-2">
                          {nursery.nursery_org_name}
                        </p>
                        <div className="space-y-1 text-sm text-neutral-600">
                          {nursery.min_order_qty && (
                            <p>Min order: {nursery.min_order_qty} trees</p>
                          )}
                          {nursery.lead_time_months && (
                            <p>Lead time: {nursery.lead_time_months} months</p>
                          )}
                          {nursery.rootstock_options && nursery.rootstock_options.length > 0 && (
                            <p>Rootstocks: {nursery.rootstock_options.join(', ')}</p>
                          )}
                        </div>
                        <span className={`
                          mt-3 inline-block px-2 py-0.5 text-xs font-medium rounded-full
                          ${nursery.availability_status === 'available'
                            ? 'bg-success-100 text-success-700'
                            : 'bg-neutral-100 text-neutral-600'
                          }
                        `}>
                          {nursery.availability_status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Guides Tab */}
          {activeTab === 'guides' && (
            <Card>
              <CardHeader>
                <CardTitle>Management Guides</CardTitle>
              </CardHeader>
              <CardContent>
                {guides.length === 0 ? (
                  <div className="text-center py-12">
                    <BookOpen className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
                    <p className="text-neutral-500">No management guides available for this cultivar</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {guides.map((guide) => (
                      <div
                        key={guide.id}
                        className="p-4 border border-neutral-200 rounded-lg hover:border-primary-300 transition-colors cursor-pointer"
                        onClick={() => navigate(`/cultivar/guides/${guide.id}`)}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-neutral-900">{guide.title}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
                                {guide.category}
                              </span>
                              {guide.region && (
                                <span className="text-xs text-neutral-500">{guide.region}</span>
                              )}
                            </div>
                          </div>
                          <ExternalLink className="h-4 w-4 text-neutral-400" />
                        </div>
                        {guide.content && (
                          <p className="mt-2 text-sm text-neutral-600 line-clamp-2">
                            {guide.content.substring(0, 200)}...
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {activeTab === 'media' && (
            <MediaTab cultivarId={Number(id)} isOwner={canEdit} />
          )}

          {activeTab === 'observations' && (
            <div className="space-y-6">
              <AttributeValuesTab cultivarId={Number(id)} cultivarName={cultivar.name} />
              {/* Maturity is one of the readings above, rolled up per region and
                  season. It sits here rather than on a tab of its own so the
                  dates and the observations behind them stay in one place. */}
              <MaturityByRegionPanel
                cultivarId={Number(id)}
                cultivarName={cultivar.name}
                canEdit={canEdit}
              />
            </div>
          )}

          {activeTab === 'evaluators' && canEdit && (
            <EvaluatorsTab cultivarId={Number(id)} />
          )}

          {activeTab === 'visibility' && canEdit && (
            <VisibilityTab cultivarId={Number(id)} initial={cultivar.visibility_config || {}} />
          )}

          {/* Audit Tab */}
          {activeTab === 'audit' && (
            <Card>
              <CardHeader>
                <CardTitle>Audit History</CardTitle>
              </CardHeader>
              <CardContent>
                <AuditTimeline entityType="cultivar" entityId={Number(id)} />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

export default CultivarDetail;
